import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Wallet, Snowflake, Flame, Fuel, Globe, Plus, RefreshCw, ExternalLink, Star, Trash2,
  ArrowLeftRight, Download, Copy, Check, ShieldAlert, ArrowDownToLine, ArrowUpFromLine,
  Waves, Send, AlertTriangle, Layers,
} from 'lucide-react'
import { PageHead } from '../AdminLayout'
import {
  Panel, DataTable, Pager, Toolbar, Select, SearchBox, RefreshButton, Stat, Tag, Status,
  ConfirmButton, Drawer, BarList,
} from '../ui'
import { get, post, patch, del, qs } from '../../lib/api'
import { toast } from '../../store'
import { clsx, compact, fa, faDate, num, shortAddr } from '../../lib/format'

const KIND_ICON: Record<string, any> = { hot: Flame, cold: Snowflake, fee: Fuel, external: Globe }
const KIND_COLOR: Record<string, string> = {
  hot: '#FF4D6D', cold: '#22D3EE', fee: '#FFB020', external: '#7C5CFF', deposit: '#00E39A',
}

const TABS: [string, string, any][] = [
  ['overview', 'نمای کلی خزانه', Layers],
  ['wallets', 'کیف پول‌ها', Wallet],
  ['deposits', 'آدرس‌های کاربران', ArrowDownToLine],
  ['transfers', 'انتقال بین کیف‌ها', ArrowLeftRight],
  ['ledger', 'دفتر کل حرکت‌ها', Waves],
]

function CopyAddr({ value }: { value: string }) {
  const [done, setDone] = useState(false)
  return (
    <button onClick={(e) => {
      e.stopPropagation()
      navigator.clipboard?.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500)
    }} className="text-slate-500 transition hover:text-brand-300">
      {done ? <Check className="h-3.5 w-3.5 text-up" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

export default function Wallets() {
  const [tab, setTab] = useState('overview')
  return (
    <>
      <PageHead title="کیف پول‌ها و دارایی‌ها"
        subtitle="همه کیف پول‌های صرافی، موجودی واقعی آن‌ها و تمام حرکت‌های زنجیره‌ای" />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={clsx('flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition',
              tab === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-200')}>
            <Icon className="h-3.5 w-3.5" /> {l}
          </button>
        ))}
      </div>
      {tab === 'overview' && <Overview />}
      {tab === 'wallets' && <WalletList />}
      {tab === 'deposits' && <DepositAddresses />}
      {tab === 'transfers' && <Transfers />}
      {tab === 'ledger' && <Ledger />}
    </>
  )
}

/* ═══════════════════════════════════════════════ نمای کلی */
function Overview() {
  const [d, setD] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get('/admin/wallets/overview').then(setD).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(() => {
    load()
    const id = setInterval(load, 45000)
    return () => clearInterval(id)
  }, [])

  const refreshAll = async () => {
    setLoading(true)
    try { const r: any = await post('/admin/wallets/refresh-all'); toast.ok(`موجودی ${fa(r.wallets)} کیف پول به‌روز شد`); load() }
    catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  const t = d?.totals ?? {}
  const coverage = t.coverage

  return (
    <>
      {d?.chain && !d.chain.enabled && (
        <div className="card mb-5 border-down/30 bg-down/[.06] p-4">
          <div className="mb-2 flex items-center gap-2 font-bold text-down">
            <AlertTriangle className="h-5 w-5" /> هیچ شبکه بلاک‌چینی فعال نیست
          </div>
          <p className="mb-3 text-xs leading-7 text-slate-300">
            تا وقتی این مشکل حل نشود، فرم انتقال شبکه و ارزی نشان نمی‌دهد و بخش
            «واریز و برداشت رمزارز» برای کاربران غیرفعال است. دلیل دقیق:
          </p>
          <div className="space-y-1.5">
            {d.chain.errors.map((e: any) => (
              <div key={e.network} className="rounded-lg border border-white/[.06] bg-ink-950/50 p-2.5 text-[11px]">
                <span className="num ml-2 text-slate-500">
                  {e.network === '*' ? 'کل بخش زنجیره‌ای' : e.network}:
                </span>
                <span className="text-down">{e.message}</span>
              </div>
            ))}
            {d.chain.errors.length === 0 && (
              <div className="text-[11px] text-slate-400">
                دلیلی گزارش نشده — لاگ سرویس را بررسی کنید.
              </div>
            )}
          </div>
          <p className="mt-3 text-[11px] leading-6 text-slate-500">
            بررسی کامل روی سرور:{' '}
            <code className="num rounded bg-white/[.05] px-1.5 py-0.5 text-brand-200">
              docker compose exec backend python -m app.scripts.checkenv
            </code>
          </p>
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Tag kind={d?.mainnet ? 'down' : 'gold'}>{d?.mainnet ? 'شبکه اصلی' : 'شبکه آزمایشی'}</Tag>
        <Tag kind="muted">{fa(d?.walletCount ?? 0)} کیف پول فعال</Tag>
        <Tag kind="muted">{fa(d?.depositAddressCount ?? 0)} آدرس واریز کاربر</Tag>
        {d?.lastUpdate && <Tag kind="info">آخرین به‌روزرسانی: {faDate(d.lastUpdate)}</Tag>}
        <button onClick={refreshAll} className="btn-ghost mr-auto !py-2 text-xs">
          <RefreshCw className={clsx('h-3.5 w-3.5', loading && 'animate-spin')} /> به‌روزرسانی موجودی‌ها
        </button>
      </div>

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="بدهی به کاربران" value={compact(t.liability)} color="#FF4D6D"
          icon={<ShieldAlert className="h-5 w-5" />} hint="مجموع موجودی کیف پول همه کاربران" />
        <Stat label="دارایی واقعی صرافی" value={compact(t.held)} color="#00E39A"
          icon={<Wallet className="h-5 w-5" />} hint="گرم + سرد + کارمزد + آدرس واریز" />
        <Stat label="پوشش دارایی" value={coverage === null || coverage === undefined ? '—' : `${num(coverage, 1)}٪`}
          color={coverage >= 100 ? '#00E39A' : coverage >= 50 ? '#FFB020' : '#FF4D6D'}
          icon={<Layers className="h-5 w-5" />} hint="نسبت دارایی نگهداری‌شده به بدهی" />
        <Stat label="موجودی کیف پول گرم" value={compact(t.hot)} color="#22D3EE"
          icon={<Flame className="h-5 w-5" />} hint="منبع پرداخت برداشت‌ها" />
      </div>

      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(['hot', 'cold', 'fee', 'deposit'] as const).map((k) => {
          const Icon = k === 'deposit' ? ArrowDownToLine : KIND_ICON[k]
          const label = { hot: 'کیف پول گرم', cold: 'کیف پول سرد', fee: 'کیف پول کارمزد', deposit: 'آدرس‌های واریز کاربران' }[k]
          const share = t.held ? ((t[k] ?? 0) / t.held) * 100 : 0
          return (
            <div key={k} className="card p-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-lg"
                  style={{ background: `${KIND_COLOR[k]}1A`, border: `1px solid ${KIND_COLOR[k]}44`, color: KIND_COLOR[k] }}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-xs text-slate-300">{label}</span>
              </div>
              <div className="num mb-2 text-lg font-bold text-white">{compact(t[k])}</div>
              <div className="mb-1 h-1.5 overflow-hidden rounded-full bg-white/[.06]">
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${Math.min(share, 100)}%`, background: KIND_COLOR[k] }} />
              </div>
              <div className="num text-[10px] text-slate-500">{num(share, 1)}٪ از کل دارایی</div>
            </div>
          )
        })}
      </div>

      {coverage !== null && coverage !== undefined && coverage < 100 && (
        <div className="mb-5 flex items-start gap-2 rounded-2xl border border-gold/25 bg-gold/[.07] p-4 text-xs leading-6 text-gold">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          دارایی نگهداری‌شده کمتر از بدهی به کاربران است. اگر بخشی از دارایی در کیف پول سردِ
          ثبت‌نشده نگهداری می‌شود، آن آدرس را در تب «کیف پول‌ها» به‌عنوان کیف سرد اضافه کنید تا
          در این محاسبه دیده شود.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="پوشش دارایی به تفکیک ارز" dense className="lg:col-span-2">
          <DataTable rows={d?.assets ?? null} minWidth={820} empty="داده‌ای موجود نیست" columns={[
            { key: 's', header: 'ارز', cell: (r: any) => <span className="num text-slate-100">{r.symbol}</span> },
            { key: 'l', header: 'بدهی به کاربران', cell: (r: any) => <span className="num text-white">{num(r.liability, 8)}</span> },
            { key: 'h', header: 'گرم', cell: (r: any) => <span className="num text-slate-300">{num(r.hot, 8)}</span> },
            { key: 'c', header: 'سرد', cell: (r: any) => <span className="num text-aqua">{num(r.cold, 8)}</span> },
            { key: 'f', header: 'کارمزد', cell: (r: any) => <span className="num text-gold">{num(r.fee, 8)}</span> },
            { key: 'dp', header: 'آدرس واریز', cell: (r: any) => <span className="num text-up">{num(r.deposit, 8)}</span> },
            {
              key: 'cv', header: 'پوشش', cell: (r: any) => r.coverage === null
                ? <Tag kind="muted">—</Tag>
                : <Tag kind={r.coverage >= 100 ? 'up' : r.coverage >= 50 ? 'gold' : 'down'}>{num(r.coverage, 1)}٪</Tag>,
            },
            {
              key: 'g', header: 'اختلاف', cell: (r: any) => (
                <span className={clsx('num', r.gap >= 0 ? 'text-up' : 'text-down')}>
                  {r.gap >= 0 ? '+' : ''}{num(r.gap, 8)}
                </span>
              ),
            },
          ]} />
        </Panel>

        <Panel title="دارایی به تفکیک خانواده شبکه">
          <BarList items={(d?.families ?? []).map((f: any) => ({
            label: `${f.familyLabel} · ${f.networks.join('، ')}`, value: f.totalIrt,
          }))} format={compact} />
          {!(d?.families ?? []).length && <p className="py-6 text-center text-xs text-slate-500">موجودی ثبت نشده است</p>}
        </Panel>
      </div>
    </>
  )
}

/* ═══════════════════════════════════════════════ فهرست کیف پول‌ها */
function WalletList() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [f, setF] = useState({ kind: '', family: '', q: '' })
  const [loading, setLoading] = useState(false)
  const [create, setCreate] = useState(false)
  const [detail, setDetail] = useState<any>(null)
  const [transferFrom, setTransferFrom] = useState<any>(null)
  const [meta, setMeta] = useState<any>(null)

  const load = () => {
    setLoading(true)
    get(`/admin/wallets${qs(f)}`).then(setRows).catch(() => setRows([])).finally(() => setLoading(false))
    get('/admin/wallets/overview').then(setMeta).catch(() => {})
  }
  useEffect(() => { const id = setTimeout(load, f.q ? 350 : 0); return () => clearTimeout(id) }, [f])

  const refreshOne = async (w: any) => {
    try { await post(`/admin/wallets/${w.id}/refresh`); toast.ok('موجودی به‌روز شد'); load() }
    catch (e: any) { toast.err(e.message) }
  }

  return (
    <>
      <Toolbar>
        <SearchBox value={f.q} onChange={(q) => setF({ ...f, q })} placeholder="آدرس یا عنوان کیف پول…" />
        <Select value={f.kind} onChange={(kind) => setF({ ...f, kind })}
          options={[['', 'همه انواع'], ['hot', 'گرم'], ['cold', 'سرد'], ['fee', 'کارمزد'], ['external', 'بیرونی']]} />
        <Select value={f.family} onChange={(family) => setF({ ...f, family })}
          options={[['', 'همه شبکه‌ها'], ...((meta?.availableFamilies ?? []).map((x: any) => [x.key, x.label] as [string, string]))]} />
        <RefreshButton onClick={load} loading={loading} />
        <button onClick={() => setCreate(true)} className="btn-primary !py-2 text-xs">
          <Plus className="h-3.5 w-3.5" /> افزودن کیف پول
        </button>
      </Toolbar>

      {rows === null ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-52 w-full" />)}
        </div>
      ) : rows.length === 0 ? (
        <Panel><p className="py-10 text-center text-xs text-slate-500">کیف پولی یافت نشد</p></Panel>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          {rows.map((w) => {
            const Icon = KIND_ICON[w.kind] ?? Wallet
            const color = KIND_COLOR[w.kind] ?? '#7C5CFF'
            return (
              <div key={w.id} className="card p-4">
                <div className="mb-3 flex items-start gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
                    style={{ background: `${color}1A`, border: `1px solid ${color}44`, color }}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="truncate text-sm font-bold text-white">{w.label}</span>
                      {w.isPrimary && <Tag kind="gold"><Star className="ml-1 h-3 w-3" />پیش‌فرض</Tag>}
                      {!w.active && <Tag kind="muted">غیرفعال</Tag>}
                      {w.watchOnly && <Tag kind="info">فقط‌خواندنی</Tag>}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      {w.kindLabel} · {w.familyLabel}
                      {w.index !== null && <span className="num"> · اندیس {fa(w.index)}</span>}
                    </div>
                  </div>
                  <div className="text-left">
                    <div className="num text-sm font-bold text-white">{compact(w.totalIrt)}</div>
                    <div className="text-[10px] text-slate-500">تومان</div>
                  </div>
                </div>

                <div className="mb-3 flex items-center gap-2 rounded-xl border border-white/[.06] bg-ink-950/50 px-3 py-2">
                  <span className="num flex-1 truncate text-[11px] text-slate-300" dir="ltr">{w.address}</span>
                  <CopyAddr value={w.address} />
                  {w.explorer && (
                    <a href={w.explorer} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-brand-300">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>

                <div className="mb-3 min-h-[3.5rem] space-y-1">
                  {w.assets.length === 0 && <p className="py-3 text-center text-[11px] text-slate-600">موجودی صفر</p>}
                  {w.assets.slice(0, 5).map((a: any) => (
                    <div key={a.network + a.symbol} className="flex items-center gap-2 text-[11px]">
                      <Tag kind="muted">{a.network}</Tag>
                      <span className="text-slate-300">{a.symbol}</span>
                      <span className="num mr-auto text-slate-200">{num(a.amount, 8)}</span>
                      <span className="num w-20 text-left text-slate-500">{compact(a.valueIrt)}</span>
                    </div>
                  ))}
                  {w.assets.length > 5 && (
                    <div className="text-center text-[10px] text-slate-500">
                      و {fa(w.assets.length - 5)} دارایی دیگر…
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => setDetail(w)} className="btn-ghost !px-2.5 !py-1.5 text-[11px]">جزئیات</button>
                  <button onClick={() => refreshOne(w)} className="btn-ghost !px-2.5 !py-1.5 text-[11px]">
                    <RefreshCw className="h-3 w-3" /> موجودی
                  </button>
                  {!w.watchOnly && (
                    <button onClick={() => setTransferFrom(w)} className="btn-ghost !px-2.5 !py-1.5 text-[11px]">
                      <Send className="h-3 w-3" /> انتقال
                    </button>
                  )}
                  {w.kind === 'hot' && !w.isPrimary && !w.watchOnly && (
                    <ConfirmButton variant="ghost" label="پرداخت از این کیف" question="پیش‌فرض شود؟"
                      icon={<Star className="h-3 w-3" />}
                      onConfirm={async () => {
                        try { await patch(`/admin/wallets/${w.id}`, { make_primary: true }); toast.ok('کیف پول پیش‌فرض تغییر کرد'); load() }
                        catch (e: any) { toast.err(e.message) }
                      }} />
                  )}
                  {w.watchOnly && (
                    <ConfirmButton variant="ghost" label="حذف" question="حذف شود؟"
                      icon={<Trash2 className="h-3 w-3" />}
                      onConfirm={async () => {
                        try { await del(`/admin/wallets/${w.id}`); toast.ok('حذف شد'); load() }
                        catch (e: any) { toast.err(e.message) }
                      }} />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <CreateWalletDrawer open={create} onClose={() => setCreate(false)}
        families={meta?.availableFamilies ?? []} onDone={load} />
      <WalletDetail wallet={detail} onClose={() => setDetail(null)} />
      <TransferDrawer wallet={transferFrom} wallets={rows ?? []}
        onClose={() => setTransferFrom(null)} onDone={load} />
    </>
  )
}

function CreateWalletDrawer({ open, onClose, families, onDone }:
  { open: boolean; onClose: () => void; families: any[]; onDone: () => void }) {
  const [f, setF] = useState({ kind: 'hot', family: '', label: '', address: '', note: '' })
  const [saving, setSaving] = useState(false)
  useEffect(() => { if (open && families[0]) setF((s) => ({ ...s, family: s.family || families[0].key })) }, [open, families])

  const needsAddress = f.kind === 'cold' || f.kind === 'external'

  const submit = async () => {
    setSaving(true)
    try {
      await post('/admin/wallets', f)
      toast.ok('کیف پول ثبت شد'); setF({ ...f, label: '', address: '', note: '' }); onDone(); onClose()
    } catch (e: any) { toast.err(e.message) } finally { setSaving(false) }
  }

  return (
    <Drawer open={open} onClose={onClose} title="افزودن کیف پول">
      <label className="label">نوع کیف پول</label>
      <div className="mb-4 grid grid-cols-2 gap-2">
        {([['hot', 'گرم (کلید در سرور)'], ['fee', 'کارمزد / گس'],
           ['cold', 'سرد (فقط آدرس)'], ['external', 'آدرس بیرونی']] as const).map(([v, l]) => {
          const Icon = KIND_ICON[v]
          return (
            <button key={v} onClick={() => setF({ ...f, kind: v })}
              className={clsx('flex items-center gap-2 rounded-xl border p-3 text-right text-xs transition',
                f.kind === v ? 'border-brand-500/50 bg-brand-500/10 text-brand-200'
                  : 'border-white/10 text-slate-400 hover:border-white/25')}>
              <Icon className="h-4 w-4 shrink-0" /> {l}
            </button>
          )
        })}
      </div>

      <div className="mb-4 rounded-xl border border-white/[.06] bg-white/[.02] p-3 text-[11px] leading-6 text-slate-400">
        {f.kind === 'hot' && 'یک آدرس تازه از کیف پول HD ساخته می‌شود (اندیس عملیاتی ۱ تا ۹۹۹). کلید خصوصی آن در سرور در دسترس است و می‌تواند برداشت پرداخت کند.'}
        {f.kind === 'fee' && 'کیف پول عملیاتی برای نگهداری ارز بومی شبکه و پرداخت گس/انرژی. کلید در سرور است.'}
        {f.kind === 'cold' && 'فقط آدرس ثبت می‌شود؛ کلید خصوصی در سرور نیست. برای نگهداری امن مازاد دارایی و دیده‌شدن در گزارش پوشش.'}
        {f.kind === 'external' && 'یک آدرس بیرونی برای رصد (مثلاً حساب صرافی دیگر یا شریک).'}
      </div>

      <label className="label">خانواده شبکه</label>
      <select className="input mb-3" value={f.family} onChange={(e) => setF({ ...f, family: e.target.value })}>
        {families.map((x: any) => (
          <option key={x.key} value={x.key} className="bg-ink-900">
            {x.label} ({x.networks.join('، ')})
          </option>
        ))}
      </select>

      <label className="label">عنوان</label>
      <input className="input mb-3" placeholder="مثلاً کیف سرد اصلی" value={f.label}
        onChange={(e) => setF({ ...f, label: e.target.value })} />

      {needsAddress && (
        <>
          <label className="label">آدرس</label>
          <input className="input num mb-3" dir="ltr" value={f.address}
            onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="آدرس کیف پول" />
        </>
      )}

      <label className="label">یادداشت</label>
      <input className="input mb-4" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />

      <button onClick={submit} disabled={saving || !f.family || (needsAddress && !f.address)}
        className="btn-primary w-full !py-3">ثبت کیف پول</button>
    </Drawer>
  )
}

function WalletDetail({ wallet, onClose }: { wallet: any; onClose: () => void }) {
  const [d, setD] = useState<any>(null)
  useEffect(() => {
    if (!wallet) return setD(null)
    get(`/admin/wallets/${wallet.id}`).then(setD).catch(() => {})
  }, [wallet])

  return (
    <Drawer open={!!wallet} onClose={onClose} wide title={wallet?.label ?? 'کیف پول'}>
      {!d ? <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-14 w-full" />)}</div> : (
        <>
          <div className="mb-4 rounded-2xl border border-white/[.07] bg-ink-950/60 p-4">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Tag kind="info">{d.kindLabel}</Tag>
              <Tag kind="muted">{d.familyLabel}</Tag>
              {d.isPrimary && <Tag kind="gold">پیش‌فرض پرداخت</Tag>}
              {d.watchOnly && <Tag kind="info">فقط‌خواندنی</Tag>}
              {d.index !== null && <Tag kind="muted">اندیس {fa(d.index)}</Tag>}
            </div>
            <div className="num mb-2 break-all text-xs text-slate-200" dir="ltr">{d.address}</div>
            <div className="flex flex-wrap items-center gap-2">
              <CopyAddr value={d.address} />
              {d.explorer && (
                <a href={d.explorer} target="_blank" rel="noreferrer" className="btn-ghost !px-2.5 !py-1.5 text-[11px]">
                  اکسپلورر <ExternalLink className="h-3 w-3" />
                </a>
              )}
              <span className="num mr-auto text-sm font-bold text-white">{compact(d.totalIrt)} تومان</span>
            </div>
            {d.note && <p className="mt-2 text-[11px] leading-6 text-slate-500">{d.note}</p>}
          </div>

          <h4 className="mb-2 text-xs font-bold text-white">موجودی دارایی‌ها</h4>
          <div className="mb-5 space-y-1.5">
            {d.assets.length === 0 && <p className="py-4 text-center text-xs text-slate-500">موجودی صفر است</p>}
            {d.assets.map((a: any) => (
              <div key={a.network + a.symbol}
                className="flex items-center gap-2 rounded-xl border border-white/[.06] bg-white/[.02] p-2.5 text-xs">
                <Tag kind="muted">{a.network}</Tag>
                <span className="text-slate-200">{a.symbol}</span>
                <span className="num mr-auto text-white">{num(a.amount, 8)}</span>
                <span className="num w-24 text-left text-slate-500">{compact(a.valueIrt)}</span>
                {a.error && <span className="text-[10px] text-down">خطا</span>}
              </div>
            ))}
          </div>

          {d.transfers?.length > 0 && (
            <>
              <h4 className="mb-2 text-xs font-bold text-white">انتقال‌های مرتبط</h4>
              <div className="mb-5 space-y-1.5">
                {d.transfers.map((t: any) => (
                  <div key={t.id} className="rounded-xl border border-white/[.06] bg-white/[.02] p-2.5 text-[11px]">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="num text-white">{num(t.amount, 8)} {t.symbol}</span>
                      <Status value={t.status} />
                      <span className="mr-auto text-slate-500">{faDate(t.createdAt)}</span>
                    </div>
                    <div className="num truncate text-slate-500" dir="ltr">→ {shortAddr(t.toAddress, 10)}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {d.payouts?.length > 0 && (
            <>
              <h4 className="mb-2 text-xs font-bold text-white">آخرین برداشت‌های پرداخت‌شده از این کیف</h4>
              <div className="space-y-1.5">
                {d.payouts.map((p: any) => (
                  <div key={p.id} className="flex items-center gap-2 rounded-xl border border-white/[.06] bg-white/[.02] p-2.5 text-[11px]">
                    <span className="num text-down">−{num(p.amount, 8)} {p.symbol}</span>
                    <Status value={p.status} />
                    <span className="num mr-auto truncate text-slate-500" dir="ltr">{shortAddr(p.to, 8)}</span>
                    {p.explorer && (
                      <a href={p.explorer} target="_blank" rel="noreferrer" className="text-brand-300">
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Drawer>
  )
}

/* ═══════════════════════════════════════════════ انتقال */
function TransferDrawer({ wallet, wallets, onClose, onDone }:
  { wallet: any; wallets: any[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState<any>({ network: '', symbol: '', amount: '', to_wallet_id: '', to_address: '', note: '' })
  const [saving, setSaving] = useState(false)

  const targets = wallets.filter((w) => w.family === wallet?.family && w.id !== wallet?.id)

  // فهرست ارزهای قابل انتخاب از «ارزهای پشتیبانی‌شده شبکه» می‌آید، نه از موجودی‌های
  // اسکن‌شده. اگر از assets پر شود، کیف پول تازه‌ساخته یا هنوز اسکن‌نشده هیچ ارزی
  // برای انتخاب نشان نمی‌دهد.
  const options = useMemo(() => {
    const sup = wallet?.supported?.[f.network]
    if (sup?.length) return sup
    // سازگاری با پاسخ‌های قدیمی بک‌اند
    const seen = new Set<string>()
    return (wallet?.assets ?? [])
      .filter((a: any) => a.network === f.network && !seen.has(a.symbol) && seen.add(a.symbol))
      .map((a: any) => ({ symbol: a.symbol, balance: a.amount, kind: '' }))
  }, [wallet, f.network])

  useEffect(() => {
    if (!wallet) return
    const net = wallet.networks?.[0] ?? ''
    const first = wallet.supported?.[net]?.[0]?.symbol
      ?? wallet.assets?.find((a: any) => a.network === net)?.symbol
      ?? wallet.assets?.[0]?.symbol ?? ''
    setF({ network: net, symbol: first, amount: '', to_wallet_id: '', to_address: '', note: '' })
  }, [wallet])

  // با عوض‌شدن شبکه، اگر ارز فعلی روی شبکه جدید وجود ندارد، اولین ارز آن انتخاب می‌شود
  useEffect(() => {
    if (!options.length) return
    if (!options.some((o: any) => o.symbol === f.symbol)) {
      setF((p: any) => ({ ...p, symbol: options[0].symbol, amount: '' }))
    }
  }, [options])

  const available = useMemo(
    () => options.find((o: any) => o.symbol === f.symbol)?.balance ?? 0,
    [options, f.symbol],
  )

  const submit = async () => {
    setSaving(true)
    try {
      await post('/admin/wallets/transfer', {
        from_wallet_id: wallet.id,
        to_wallet_id: f.to_wallet_id ? Number(f.to_wallet_id) : null,
        to_address: f.to_address, network: f.network, symbol: f.symbol,
        amount: Number(f.amount), note: f.note,
      })
      toast.ok('انتقال در صف ارسال قرار گرفت')
      onDone(); onClose()
    } catch (e: any) { toast.err(e.message) } finally { setSaving(false) }
  }

  return (
    <Drawer open={!!wallet} onClose={onClose} title={`انتقال از ${wallet?.label ?? ''}`}>
      <div className="mb-4 rounded-xl border border-down/25 bg-down/[.07] p-3 text-[11px] leading-6 text-down">
        <AlertTriangle className="ml-1 inline h-3.5 w-3.5" />
        این یک تراکنش واقعی روی بلاک‌چین است و برگشت‌ناپذیر خواهد بود.
      </div>

      {(wallet?.networks ?? []).length === 0 && (
        <div className="mb-4 rounded-xl border border-down/30 bg-down/[.08] p-3 text-[11px] leading-6 text-down">
          <AlertTriangle className="ml-1 inline h-3.5 w-3.5" />
          هیچ شبکه‌ای از خانواده «{wallet?.familyLabel}» فعال نیست، پس انتقال ممکن نیست.
          علت هر شبکه:
          <ul className="mt-1.5 space-y-1 pr-4">
            {(wallet?.inactiveNetworks ?? []).map((n: any) => (
              <li key={n.key} className="list-disc">
                <b>{n.key}</b> ({n.faName}) — {n.error || 'در دسترس نیست'}
              </li>
            ))}
          </ul>
          <div className="mt-2 opacity-80">
            معمولاً یعنی RPC آن شبکه در فایل <code>.env</code> پر نشده است.
          </div>
        </div>
      )}

      <label className="label">شبکه</label>
      <select className="input mb-3" value={f.network} onChange={(e) => setF({ ...f, network: e.target.value })}>
        {(wallet?.networks ?? []).length === 0 && (
          <option value="" className="bg-ink-900">— شبکه فعالی وجود ندارد —</option>
        )}
        {(wallet?.networks ?? []).map((n: string) => <option key={n} value={n} className="bg-ink-900">{n}</option>)}
      </select>

      <label className="label">ارز</label>
      <select className="input mb-1" value={f.symbol} onChange={(e) => setF({ ...f, symbol: e.target.value, amount: '' })}>
        {options.length === 0 && <option value="" className="bg-ink-900">— ارزی روی این شبکه فعال نیست —</option>}
        {options.map((o: any) => (
          <option key={o.symbol} value={o.symbol} className="bg-ink-900">
            {o.symbol}{o.balance > 0 ? ` — موجودی ${num(o.balance, 8)}` : ' — موجودی ۰'}
          </option>
        ))}
      </select>
      {options.length > 0 && available === 0 && (
        <div className="mb-3 flex items-start gap-1.5 rounded-lg border border-gold/25 bg-gold/[.06] p-2 text-[10px] leading-5 text-gold">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          موجودی این ارز صفر است. اگر تازه شارژ کرده‌اید، اول روی «به‌روزرسانی» همین کیف پول بزنید.
        </div>
      )}
      {options.length === 0 && (
        <div className="mb-3 rounded-lg border border-down/25 bg-down/[.06] p-2 text-[10px] leading-5 text-down">
          هیچ ارزی برای این شبکه تعریف نشده یا شبکه در دسترس نیست؛ بخش «شبکه‌های بلاک‌چین» را بررسی کنید.
        </div>
      )}

      <label className="label">مقصد</label>
      <select className="input mb-2" value={f.to_wallet_id}
        onChange={(e) => setF({ ...f, to_wallet_id: e.target.value, to_address: '' })}>
        <option value="" className="bg-ink-900">— آدرس دستی —</option>
        {targets.map((w) => (
          <option key={w.id} value={w.id} className="bg-ink-900">{w.kindLabel} · {w.label}</option>
        ))}
      </select>
      {!f.to_wallet_id && (
        <input className="input num mb-3" dir="ltr" placeholder="آدرس مقصد" value={f.to_address}
          onChange={(e) => setF({ ...f, to_address: e.target.value })} />
      )}

      <label className="label">مقدار</label>
      <input className="input num" dir="ltr" value={f.amount}
        onChange={(e) => setF({ ...f, amount: e.target.value.replace(/[^\d.]/g, '') })} />
      <div className="mb-3 mt-1.5 flex items-center gap-2">
        <span className="num text-[11px] text-slate-500">موجودی: {num(available, 8)} {f.symbol}</span>
        <button onClick={() => setF({ ...f, amount: String(available) })}
          className="btn-ghost !px-2 !py-1 text-[10px]">حداکثر</button>
      </div>

      <label className="label">یادداشت</label>
      <input className="input mb-4" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })}
        placeholder="مثلاً انتقال مازاد به کیف سرد" />

      <button onClick={submit} disabled={saving || !Number(f.amount) || (!f.to_wallet_id && !f.to_address)}
        className="btn-primary w-full !py-3">
        <Send className="h-4 w-4" /> ثبت انتقال
      </button>
    </Drawer>
  )
}

function Transfers() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/wallets/transfers/list${qs({ status, page, per_page: 25 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [status, page])

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <Select value={status} onChange={(v) => { setStatus(v); setPage(1) }}
            options={[['', 'همه'], ['queued', 'در صف'], ['sending', 'در حال ارسال'], ['sent', 'ارسال شده'],
                      ['confirmed', 'تأییدشده'], ['failed', 'ناموفق']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={980} empty="انتقالی ثبت نشده است" columns={[
        { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'n', header: 'شبکه', cell: (r: any) => <Tag kind="muted">{r.networkName}</Tag> },
        {
          key: 'a', header: 'مقدار', cell: (r: any) => (
            <div><div className="num text-white">{num(r.amount, 8)} {r.symbol}</div>
              <div className="num text-[10px] text-slate-500">{compact(r.valueIrt)} تومان</div></div>
          ),
        },
        { key: 'fr', header: 'از', cell: (r: any) => <div><div className="text-slate-300">{r.fromWallet}</div><div className="num text-[10px] text-slate-500" dir="ltr">{shortAddr(r.fromAddress, 6)}</div></div> },
        { key: 'to', header: 'به', cell: (r: any) => <div><div className="text-slate-300">{r.toWallet || 'آدرس دستی'}</div><div className="num text-[10px] text-slate-500" dir="ltr">{shortAddr(r.toAddress, 6)}</div></div> },
        {
          key: 's', header: 'وضعیت', cell: (r: any) => (
            <div className="space-y-1"><Status value={r.status} />
              {r.error && <div className="max-w-[12rem] truncate text-[10px] text-down">{r.error}</div>}</div>
          ),
        },
        {
          key: 'tx', header: 'تراکنش', cell: (r: any) => r.explorer
            ? <a href={r.explorer} target="_blank" rel="noreferrer" className="num inline-flex items-center gap-1 text-brand-300 hover:underline" dir="ltr">{shortAddr(r.txid, 6)}<ExternalLink className="h-3 w-3" /></a>
            : <span className="text-slate-600">—</span>,
        },
        { key: 'ad', header: 'مدیر', cell: (r: any) => <span className="num text-[11px] text-slate-500" dir="ltr">{r.admin}</span> },
        { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
        {
          key: 'x', header: '', cell: (r: any) => r.status === 'failed' && (
            <ConfirmButton variant="ghost" label="تلاش مجدد" question="دوباره؟"
              onConfirm={async () => {
                try { await post(`/admin/wallets/transfers/${r.id}/retry`); toast.ok('در صف قرار گرفت'); load() }
                catch (e: any) { toast.err(e.message) }
              }} />
          ),
        },
      ]} />
      <Pager meta={meta} onPage={setPage} />
    </Panel>
  )
}

/* ═══════════════════════════════════════════════ آدرس واریز کاربران */
function DepositAddresses() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [f, setF] = useState({ family: '', q: '', only_funded: false, page: 1 })
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/wallets/deposit-addresses${qs({ ...f, per_page: 30 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(() => { const id = setTimeout(load, f.q ? 350 : 0); return () => clearTimeout(id) }, [f])

  const total = (rows ?? []).reduce((s, r) => s + r.totalIrt, 0)

  return (
    <Panel dense
      action={<span className="num text-[11px] text-slate-500">
        مجموع موجودی این صفحه: {compact(total)} تومان
      </span>}>
      <div className="p-4 pb-0">
        <Toolbar>
          <SearchBox value={f.q} onChange={(q) => setF({ ...f, q, page: 1 })} placeholder="جستجوی آدرس…" />
          <Select value={f.family} onChange={(family) => setF({ ...f, family, page: 1 })}
            options={[['', 'همه شبکه‌ها'], ['evm', 'EVM'], ['tron', 'ترون'], ['btc', 'بیت‌کوین'], ['sol', 'سولانا']]} />
          <label className="chip cursor-pointer">
            <input type="checkbox" className="h-3 w-3 accent-brand-500" checked={f.only_funded}
              onChange={(e) => setF({ ...f, only_funded: e.target.checked, page: 1 })} />
            فقط آدرس‌های دارای موجودی
          </label>
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={900} empty="آدرسی یافت نشد" columns={[
        { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r: any) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 'f', header: 'شبکه', cell: (r: any) => <Tag kind="muted">{r.familyLabel}</Tag> },
        {
          key: 'a', header: 'آدرس', cell: (r: any) => (
            <div className="flex items-center gap-1.5">
              <span className="num text-slate-300" dir="ltr">{shortAddr(r.address, 12)}</span>
              <CopyAddr value={r.address} />
              {r.explorer && <a href={r.explorer} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-brand-300"><ExternalLink className="h-3 w-3" /></a>}
            </div>
          ),
        },
        {
          key: 'b', header: 'موجودی', cell: (r: any) => r.assets.length === 0
            ? <span className="text-slate-600">—</span>
            : (
              <div className="space-y-0.5">
                {r.assets.slice(0, 3).map((a: any) => (
                  <div key={a.network + a.symbol} className="num text-[11px] text-slate-200">
                    {num(a.amount, 6)} {a.symbol} <span className="text-slate-600">({a.network})</span>
                  </div>
                ))}
              </div>
            ),
        },
        { key: 'v', header: 'ارزش', cell: (r: any) => <span className="num text-slate-300">{r.totalIrt ? compact(r.totalIrt) : '—'}</span> },
        { key: 'i', header: 'اندیس', cell: (r: any) => <span className="num text-slate-500">{fa(r.index)}</span> },
        {
          key: 'x', header: '', cell: (r: any) => (
            <button onClick={async () => {
              try { await post(`/admin/wallets/deposit-addresses/${r.id}/refresh`); toast.ok('به‌روز شد'); load() }
              catch (e: any) { toast.err(e.message) }
            }} className="btn-ghost !px-2 !py-1.5 text-[11px]">
              <RefreshCw className="h-3 w-3" />
            </button>
          ),
        },
      ]} />
      <Pager meta={meta} onPage={(page) => setF({ ...f, page })} />
    </Panel>
  )
}

/* ═══════════════════════════════════════════════ دفتر کل */
function Ledger() {
  const [d, setD] = useState<any>(null)
  const [f, setF] = useState({ kind: '', network: '', symbol: '', status: '', q: '', days: '30', page: 1 })
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/wallets/ledger${qs({ ...f, per_page: 40 })}`)
      .then(setD).catch(() => setD({ data: [], meta: null })).finally(() => setLoading(false))
  }
  useEffect(() => { const id = setTimeout(load, f.q ? 350 : 0); return () => clearTimeout(id) }, [f])

  const exportCsv = async () => {
    const r: any = await get(`/admin/wallets/ledger/export?days=${f.days || 30}`)
    const url = URL.createObjectURL(new Blob(['﻿' + r.content], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url; a.download = r.filename; a.click()
    URL.revokeObjectURL(url)
  }

  const s = d?.summary ?? {}
  const DIR: Record<string, [string, any]> = {
    in: ['text-up', ArrowDownToLine], out: ['text-down', ArrowUpFromLine], internal: ['text-brand-200', ArrowLeftRight],
  }

  return (
    <>
      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="ورودی (واریز کاربران)" value={compact(s.in)} color="#00E39A" icon={<ArrowDownToLine className="h-5 w-5" />} />
        <Stat label="خروجی (برداشت کاربران)" value={compact(s.out)} color="#FF4D6D" icon={<ArrowUpFromLine className="h-5 w-5" />} />
        <Stat label="حرکت داخلی" value={compact(s.internal)} color="#7C5CFF" icon={<ArrowLeftRight className="h-5 w-5" />} />
        <Stat label="تعداد رکورد" value={fa(s.count ?? 0)} color="#22D3EE" />
      </div>

      <Panel dense>
        <div className="p-4 pb-0">
          <Toolbar>
            <SearchBox value={f.q} onChange={(q) => setF({ ...f, q, page: 1 })} placeholder="txid، آدرس یا ایمیل…" />
            <Select value={f.kind} onChange={(kind) => setF({ ...f, kind, page: 1 })}
              options={[['', 'همه حرکت‌ها'], ['deposit', 'واریز کاربر'], ['withdraw', 'برداشت کاربر'],
                        ['sweep', 'جمع‌آوری'], ['transfer', 'انتقال داخلی']]} />
            <Select value={f.network} onChange={(network) => setF({ ...f, network, page: 1 })}
              options={[['', 'همه شبکه‌ها'], ...((d?.networks ?? []).map((n: string) => [n, n] as [string, string]))]} />
            <Select value={f.symbol} onChange={(symbol) => setF({ ...f, symbol, page: 1 })}
              options={[['', 'همه ارزها'], ...((d?.symbols ?? []).map((n: string) => [n, n] as [string, string]))]} />
            <Select value={f.days} onChange={(days) => setF({ ...f, days, page: 1 })}
              options={[['0', 'همه زمان‌ها'], ['1', '۲۴ ساعت'], ['7', '۷ روز'], ['30', '۳۰ روز'], ['90', '۹۰ روز']]} />
            <RefreshButton onClick={load} loading={loading} />
            <button onClick={exportCsv} className="btn-ghost !py-2 text-xs">
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
          </Toolbar>
        </div>

        <DataTable rows={d?.data ?? null} loading={loading} minWidth={1020} empty="حرکتی ثبت نشده است" columns={[
          {
            key: 'k', header: 'نوع', cell: (r: any) => {
              const [color, Icon] = DIR[r.direction] ?? ['text-slate-400', ArrowLeftRight]
              return (
                <span className={clsx('inline-flex items-center gap-1.5 text-[11px] font-medium', color)}>
                  <Icon className="h-3.5 w-3.5" /> {r.kindLabel}
                </span>
              )
            },
          },
          { key: 'n', header: 'شبکه', cell: (r: any) => <Tag kind="muted">{r.network}</Tag> },
          {
            key: 'a', header: 'مقدار', cell: (r: any) => (
              <div>
                <div className={clsx('num font-semibold', r.direction === 'out' ? 'text-down' : r.direction === 'in' ? 'text-up' : 'text-slate-200')}>
                  {r.direction === 'out' ? '−' : r.direction === 'in' ? '+' : ''}{num(r.amount, 8)} {r.symbol}
                </div>
                <div className="num text-[10px] text-slate-500">{compact(r.valueIrt)} تومان</div>
              </div>
            ),
          },
          { key: 'fr', header: 'از', cell: (r: any) => <span className="num text-[11px] text-slate-500" dir="ltr">{r.from ? shortAddr(r.from, 8) : '—'}</span> },
          { key: 'to', header: 'به', cell: (r: any) => <span className="num text-[11px] text-slate-400" dir="ltr">{r.to ? shortAddr(r.to, 8) : '—'}</span> },
          {
            key: 'u', header: 'کاربر/مدیر', cell: (r: any) => r.userId
              ? <Link to={`/admin/users/${r.userId}`} className="num text-[11px] text-brand-300 hover:underline" dir="ltr">{r.email}</Link>
              : <span className="num text-[11px] text-slate-500" dir="ltr">{r.email || '—'}</span>,
          },
          { key: 's', header: 'وضعیت', cell: (r: any) => <Status value={r.status} /> },
          {
            key: 'tx', header: 'تراکنش', cell: (r: any) => r.explorer
              ? <a href={r.explorer} target="_blank" rel="noreferrer" className="num inline-flex items-center gap-1 text-brand-300 hover:underline" dir="ltr">{shortAddr(r.txid, 6)}<ExternalLink className="h-3 w-3" /></a>
              : <span className="num text-slate-600" dir="ltr">{r.txid ? shortAddr(r.txid, 6) : '—'}</span>,
          },
          { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.at)}</span> },
        ]} />
        <Pager meta={d?.meta} onPage={(page) => setF({ ...f, page })} />
      </Panel>
    </>
  )
}
